import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import {
  createCustomCodeFormGroup,
  CustomCodeFormShape,
  CustomCodeVariableDoc,
} from '../../widget-editors/custom-code/custom-code.model';
import { findDisallowedCustomCssDirective } from './custom-css-validation';

export type EventCallbackKey =
  | 'init'
  | 'ready'
  | 'show'
  | 'hide'
  | 'lineItemIncrease'
  | 'lineItemDecrease'
  | 'lineItemRemove';

export type EventCallbackFormGroup = FormGroup<{
  enabled: FormControl<boolean>;
  code: FormControl<string>;
}>;

export type CodeSlotKey =
  | 'aboveHeader'
  | 'belowHeader'
  | 'betweenLineItems'
  | 'aboveCheckoutButton'
  | 'belowCheckoutButton'
  | 'bottomOfCart'
  | 'onEmptyCart';

export const CODE_SLOT_LABELS: Record<CodeSlotKey, string> = {
  aboveHeader: 'Above Header',
  belowHeader: 'Below Header',
  betweenLineItems: 'Between Line Items',
  aboveCheckoutButton: 'Above Checkout Button',
  belowCheckoutButton: 'Below Checkout Button',
  bottomOfCart: 'Bottom Of Cart',
  onEmptyCart: 'Empty Cart',
};

export const CODE_SLOT_KEYS_IN_DISPLAY_ORDER = Object.keys(CODE_SLOT_LABELS) as CodeSlotKey[];

export interface GlobalSettingsFormShape {
  cartPageAndProductForm: FormGroup<{
    useCartPage: FormControl<boolean>;
  }>;
  buttonDesign: FormGroup<{
    backgroundColor: FormControl<string>;
    textColor: FormControl<string>;
    hoverTextColor: FormControl<string>;
    iconColor: FormControl<string>;
    cornerRadius: FormControl<number>;
  }>;
  eventCallbacks: FormGroup<Record<EventCallbackKey, EventCallbackFormGroup>>;
  customCss: FormGroup<{
    enabled: FormControl<boolean>;
    css: FormControl<string>;
  }>;
  codeSlots: FormGroup<Record<CodeSlotKey, FormGroup<CustomCodeFormShape>>>;
  override: FormGroup<{
    customTemplate: FormGroup<{
      enabled: FormControl<boolean>;
      html: FormControl<string>;
    }>;
  }>;
}

function buildCallbackStarter(handlerName: string, message: string): string {
  return `// write any JavaScript code
function ${handlerName}() {
    console.log('[PDQ Smart Cart] ${message}');
};

// and run it within the SmartCart callback
${handlerName}();`;
}

export const CALLBACK_STARTERS: Record<EventCallbackKey, string> = {
  init: buildCallbackStarter('handleInit', 'initialized'),
  ready: buildCallbackStarter('handleReady', 'ready'),
  show: buildCallbackStarter('handleShow', 'drawer opened'),
  hide: buildCallbackStarter('handleHide', 'drawer closed'),
  lineItemIncrease: buildCallbackStarter('handleLineItemIncrease', 'item increased'),
  lineItemDecrease: buildCallbackStarter('handleLineItemDecrease', 'item decreased'),
  lineItemRemove: buildCallbackStarter('handleLineItemRemove', 'item removed'),
};

const cartVariable: CustomCodeVariableDoc = {
  reference: 'cart',
  type: 'Object',
  description: 'Current Shopify cart — items, totals, discount codes, applied gift cards',
};

const itemVariable: CustomCodeVariableDoc = {
  reference: 'item',
  type: 'Object',
  description: 'The cart line item that was just modified',
};

const pdqVariable: CustomCodeVariableDoc = {
  reference: 'pdq',
  type: 'Object',
  description: 'The window.PDQ global — exposes Cart, Drawer, and config',
};

export const CALLBACK_VARIABLES: Record<EventCallbackKey, CustomCodeVariableDoc[]> = {
  init: [pdqVariable],
  ready: [cartVariable],
  show: [cartVariable],
  hide: [cartVariable],
  lineItemIncrease: [cartVariable, itemVariable],
  lineItemDecrease: [cartVariable, itemVariable],
  lineItemRemove: [cartVariable, itemVariable],
};

export const GLOBAL_CUSTOM_TEMPLATE_STARTER = `<div data-pdq-show-if="empty">
  <p>{{emptyCartText}}</p>
  <button data-pdq-action="continue-shopping">Continue shopping</button>
</div>

<div data-pdq-show-if="has-items">
  <div data-pdq-slot="cart-items-list"></div>
  <div data-pdq-slot="codes-widget"></div>

  <p>{{itemCount}} items, {{totalPrice}}</p>

  <label>
    <input type="checkbox" data-pdq-input="terms-accepted" />
    I accept the terms
  </label>

  <button data-pdq-action="checkout">Checkout</button>
  <button data-pdq-action="view-cart">View cart</button>
  <button data-pdq-action="close">Close</button>
</div>`;

const globalCustomTemplateContextVariables: CustomCodeVariableDoc[] = [
  { reference: 'items', type: 'Array', description: 'Cart line items' },
  { reference: 'itemCount', type: 'Number', description: 'Total quantity in the cart' },
  { reference: 'totalPrice', type: 'String', description: 'Cart total, money-formatted' },
  { reference: 'originalTotalPrice', type: 'String', description: 'Total before discounts, money-formatted' },
  { reference: 'totalPriceCents', type: 'Number', description: 'Cart total in cents, for your own math' },
  { reference: 'hasDiscount', type: 'Boolean', description: 'True when a discount is applied' },
  { reference: 'discountAmount', type: 'String', description: 'Discount amount, money-formatted' },
  { reference: 'emptyCartText', type: 'String', description: 'Configured empty-cart message' },
  { reference: 'emptyCartTextColor', type: 'String', description: 'Configured empty-cart text colour' },
];

// root-only tokens: widgets with no root slot are absent on purpose, line-item actions are wired there
const globalCustomTemplateTokenVariables: CustomCodeVariableDoc[] = [
  { reference: 'data-pdq-slot="cart-items-list"', type: 'Slot', description: 'Mounts the Cart Items widget' },
  { reference: 'data-pdq-slot="codes-widget"', type: 'Slot', description: 'Mounts the Discount Codes widget' },
  { reference: 'data-pdq-action="checkout"', type: 'Action', description: 'Proceeds to checkout' },
  { reference: 'data-pdq-action="view-cart"', type: 'Action', description: 'Navigates to the cart page' },
  { reference: 'data-pdq-action="continue-shopping"', type: 'Action', description: 'Closes the drawer and returns' },
  { reference: 'data-pdq-action="close"', type: 'Action', description: 'Closes the drawer' },
  { reference: 'data-pdq-input="terms-accepted"', type: 'Input', description: 'Checkbox that gates checkout' },
  { reference: 'data-pdq-show-if="empty"', type: 'Condition', description: 'Renders only when the cart is empty' },
  { reference: 'data-pdq-show-if="has-items"', type: 'Condition', description: 'Renders only when the cart has items' },
  {
    reference: 'data-pdq-line-key="{{key}}"',
    type: 'Attribute',
    description: 'Marks a row as a cart line. Every action below needs it',
  },
  { reference: 'data-pdq-action="remove-item"', type: 'Action', description: 'Removes that line from the cart' },
  {
    reference: 'data-pdq-action="increase-item"',
    type: 'Action',
    description: 'Increases that line by one, up to the Cart Items maximum',
  },
  {
    reference: 'data-pdq-action="decrease-item"',
    type: 'Action',
    description: 'Decreases that line by one, down to the Cart Items minimum',
  },
  {
    reference: 'data-pdq-action="set-quantity"',
    type: 'Action',
    description: "Sets that line to the element's value, clamped to the Cart Items min/max",
  },
  {
    reference: 'data-pdq-action="set-selling-plan"',
    type: 'Action',
    description:
      "Puts that line on the selling plan the select's value names, or one-time when empty. Needs the Cart Items Subscription control on",
  },
  {
    reference: 'data-pdq-action="set-variant"',
    type: 'Action',
    description:
      "Swaps that line to the variant the select's value names. Needs the Cart Items Change Variant control on",
  },
  {
    reference: 'data-pdq-action="set-variant-option"',
    type: 'Action',
    description:
      'Swaps that line by one option at a time for separate Dose/Flavor dropdowns. Also needs data-pdq-option-name naming which option the select controls',
  },
  {
    reference: 'data-pdq-option-name',
    type: 'Attribute',
    description: 'Names which product option a set-variant-option select controls, e.g. "Dose"',
  },
];

export const GLOBAL_CUSTOM_TEMPLATE_VARIABLES: CustomCodeVariableDoc[] = [
  ...globalCustomTemplateContextVariables,
  ...globalCustomTemplateTokenVariables,
];

export const CUSTOM_CSS_STARTER = `/* Only applies inside the Smart Cart drawer. It never leaks to the rest of the storefront. */
/* @import is rejected on save. url() must be relative, data:, or https:. */
`;

function customCssValidator(control: AbstractControl<string>): ValidationErrors | null {
  const problem = findDisallowedCustomCssDirective(control.value ?? '');
  return problem ? { disallowedCssDirective: problem } : null;
}

const GLOBAL_SETTINGS_DEFAULTS = {
  cartPageAndProductForm: { useCartPage: true },
  buttonDesign: {
    backgroundColor: '#000000',
    textColor: '#FFFFFF',
    hoverTextColor: '#000000',
    iconColor: '#FFFFFF',
    cornerRadius: 0,
  },
  customCss: { enabled: false, css: CUSTOM_CSS_STARTER },
  override: { customTemplate: { enabled: false, html: '' } },
};

function createEventCallbackGroup(starter: string): EventCallbackFormGroup {
  return new FormGroup({
    enabled: new FormControl(false, { nonNullable: true }),
    code: new FormControl(starter, { nonNullable: true }),
  });
}

function createCodeSlotsFormGroup(): FormGroup<Record<CodeSlotKey, FormGroup<CustomCodeFormShape>>> {
  return new FormGroup({
    aboveHeader: createCustomCodeFormGroup(),
    belowHeader: createCustomCodeFormGroup(),
    betweenLineItems: createCustomCodeFormGroup(),
    aboveCheckoutButton: createCustomCodeFormGroup(),
    belowCheckoutButton: createCustomCodeFormGroup(),
    bottomOfCart: createCustomCodeFormGroup(),
    onEmptyCart: createCustomCodeFormGroup(),
  });
}

export function createGlobalSettingsFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<GlobalSettingsFormShape> {
  const formGroup = new FormGroup<GlobalSettingsFormShape>({
    cartPageAndProductForm: new FormGroup({
      useCartPage: new FormControl(GLOBAL_SETTINGS_DEFAULTS.cartPageAndProductForm.useCartPage, { nonNullable: true }),
    }),
    buttonDesign: new FormGroup({
      backgroundColor: new FormControl(GLOBAL_SETTINGS_DEFAULTS.buttonDesign.backgroundColor, { nonNullable: true }),
      textColor: new FormControl(GLOBAL_SETTINGS_DEFAULTS.buttonDesign.textColor, { nonNullable: true }),
      hoverTextColor: new FormControl(GLOBAL_SETTINGS_DEFAULTS.buttonDesign.hoverTextColor, { nonNullable: true }),
      iconColor: new FormControl(GLOBAL_SETTINGS_DEFAULTS.buttonDesign.iconColor, { nonNullable: true }),
      cornerRadius: new FormControl(GLOBAL_SETTINGS_DEFAULTS.buttonDesign.cornerRadius, { nonNullable: true }),
    }),
    eventCallbacks: new FormGroup({
      init: createEventCallbackGroup(CALLBACK_STARTERS.init),
      ready: createEventCallbackGroup(CALLBACK_STARTERS.ready),
      show: createEventCallbackGroup(CALLBACK_STARTERS.show),
      hide: createEventCallbackGroup(CALLBACK_STARTERS.hide),
      lineItemIncrease: createEventCallbackGroup(CALLBACK_STARTERS.lineItemIncrease),
      lineItemDecrease: createEventCallbackGroup(CALLBACK_STARTERS.lineItemDecrease),
      lineItemRemove: createEventCallbackGroup(CALLBACK_STARTERS.lineItemRemove),
    }),
    customCss: new FormGroup({
      enabled: new FormControl(GLOBAL_SETTINGS_DEFAULTS.customCss.enabled, { nonNullable: true }),
      css: new FormControl(GLOBAL_SETTINGS_DEFAULTS.customCss.css, {
        nonNullable: true,
        validators: [customCssValidator],
      }),
    }),
    codeSlots: createCodeSlotsFormGroup(),
    override: new FormGroup({
      customTemplate: new FormGroup({
        enabled: new FormControl(GLOBAL_SETTINGS_DEFAULTS.override.customTemplate.enabled, { nonNullable: true }),
        html: new FormControl(GLOBAL_SETTINGS_DEFAULTS.override.customTemplate.html, { nonNullable: true }),
      }),
    }),
  });

  if (initialSettings) {
    formGroup.patchValue(initialSettings);
  }

  return formGroup;
}
